'use client'

import { useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { apiClient } from '@/lib/api-client'
import { useAI } from '@/context/AIContext'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { CapabilityBubbles } from './CapabilityBubbles'

// ============================================================================
// Animation Variants
// ============================================================================

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
}

const modalVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.95,
    y: 20,
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 300,
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.95,
    y: 20,
    transition: {
      duration: 0.15,
    }
  },
}

// ============================================================================
// AI Manager Component
// ============================================================================

export function AIManager() {
  const { data: session } = useSession()
  const router = useRouter()
  const {
    isManagerOpen,
    closeManager,
    chatHistory,
    addMessage,
    clearHistory,
    isTyping,
    setIsTyping,
    currentPageContext,
    pageContextData,
  } = useAI()

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [chatHistory, isTyping])

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isManagerOpen) {
        closeManager()
      }
    }
    
    if (isManagerOpen) {
      document.addEventListener('keydown', handleKeyDown)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isManagerOpen, closeManager])

  // Focus trap - focus the modal when it opens
  useEffect(() => {
    if (isManagerOpen && modalRef.current) {
      modalRef.current.focus()
    }
  }, [isManagerOpen])

  // Handle sending a message
  const handleSendMessage = useCallback(async (content: string) => {
    // Add user message
    addMessage({ role: 'user', content })

    // Add loading message
    addMessage({ role: 'assistant', content: '', isLoading: true })
    setIsTyping(true)

    try {
      // Build message history for the copilot
      const messages = chatHistory
        .filter((m: { isLoading?: boolean }) => !m.isLoading)
        .map((m: { role: string; content: string }) => ({ role: m.role, content: m.content }))
      messages.push({ role: 'user', content })

      // Call Procurement Copilot API via apiClient (handles auth + backend URL)
      const data = await apiClient.post<{
        text: string
        tool_call?: { name: string; args: Record<string, unknown>; result: unknown }
      }>('/api/procurement/copilot/chat', {
        messages,
        context: { page: currentPageContext, page_data: pageContextData },
      })

      // Map the procurement copilot response to the ChatMessage format
      const toolCalls = data.tool_call
        ? [
            {
              id: `tool-${Date.now()}`,
              name: data.tool_call.name,
              args: data.tool_call.args || {},
              result: data.tool_call.result,
            },
          ]
        : undefined

      // Remove loading message and add real response
      addMessage({
        role: 'assistant',
        content: data.text || 'I apologize, but I encountered an issue processing your request.',
        toolCalls,
      })

      // Broadcast data-changed event so other pages (PR detail, etc.) auto-refresh
      if (data.tool_call) {
        const mutatingTools = [
          'amend_purchase_request', 'create_purchase_request_draft',
          'create_po_change_request', 'create_rfq_from_pr',
          'submit_pr', 'approve_step', 'reject_step',
        ]
        if (mutatingTools.includes(data.tool_call.name)) {
          // 300ms delay ensures backend DB transaction is committed before the page re-fetches
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('procurement-data-changed', {
              detail: { tool: data.tool_call!.name, result: data.tool_call!.result },
            }))
          }, 300)
        }

        // UI control tools — drive the UI (navigate / filter / switch view).
        const uiControlTools = ['navigate_to_page', 'filter_tender_explorer', 'filter_purchase_explorer', 'switch_explorer_view', 'create_tender']
        if (uiControlTools.includes(data.tool_call.name)) {
          const result = (data.tool_call.result ?? {}) as { action?: string; path?: string }
          // Broadcast to any listening page (e.g. the Tender Explorer applies filters)
          window.dispatchEvent(new CustomEvent('ai-ui-control', {
            detail: { tool: data.tool_call.name, result: data.tool_call.result },
          }))
          // Navigation is handled here via the router
          if (result.action === 'navigate' && result.path) {
            router.push(result.path)
          }
        }
      }
    } catch {
      addMessage({
        role: 'assistant',
        content: 'I apologize, but I encountered an error connecting to Proc AI. Please try again.',
      })
    } finally {
      setIsTyping(false)
    }
  }, [addMessage, chatHistory, currentPageContext, pageContextData, setIsTyping, router])

  // Handle file upload in chat
  const handleFileUpload = useCallback(async (files: FileList) => {
    const fileNames = Array.from(files).map(f => f.name).join(', ')
    addMessage({ role: 'user', content: `📄 Uploaded: ${fileNames}` })
    addMessage({ role: 'assistant', content: '', isLoading: true })
    setIsTyping(true)

    try {
      const formData = new FormData()
      if (files.length === 1) {
        formData.append('file', files[0])
        const result = await apiClient.upload<{
          id: number
          filename: string
          document_type: string | null
          extraction_confidence: number
          summary: string | null
          extracted_fields: Record<string, unknown> | null
        }>('/api/procurement/documents/upload', formData)

        // Build a nice summary message
        const fields = result.extracted_fields || {}
        let summary = `I've analyzed **${result.filename}**`
        if (result.document_type) summary += ` (detected as **${result.document_type}**)`
        summary += ` with **${Math.round(result.extraction_confidence * 100)}%** confidence.\n\n`

        if (fields.title) summary += `**Title:** ${fields.title}\n`
        if (fields.amount) summary += `**Amount:** $${Number(fields.amount).toLocaleString()}\n`
        if (fields.vendor_name) summary += `**Vendor:** ${fields.vendor_name}\n`
        if (fields.department) summary += `**Department:** ${fields.department}\n`
        if (fields.category) summary += `**Category:** ${fields.category}\n`
        if (result.summary) summary += `\n${result.summary}\n`

        summary += `\nWould you like me to **create a Purchase Request** from this document? I can also help fill in any missing details.`

        addMessage({ role: 'assistant', content: summary })

        // Send extraction context to chat for follow-up
        const contextMsg = `The user just uploaded a document "${result.filename}" (ID: ${result.id}). Extracted data: ${JSON.stringify(fields)}. Help them create a PR from this.`
        
        // Build messages with extraction context for follow-up
        const messages = chatHistory
          .filter((m: { isLoading?: boolean }) => !m.isLoading)
          .map((m: { role: string; content: string }) => ({ role: m.role, content: m.content }))
        messages.push({ role: 'user', content: contextMsg })
      } else {
        Array.from(files).forEach(f => formData.append('files', f))
        const result = await apiClient.upload<{
          batch_id: string
          documents: Array<{ id: number; filename: string; document_type: string | null; extraction_confidence: number; summary: string | null }>
          consolidated_extraction?: Record<string, unknown>
        }>('/api/procurement/documents/upload-bulk', formData)

        let summary = `I've analyzed **${result.documents.length} documents**:\n\n`
        result.documents.forEach(doc => {
          summary += `📄 **${doc.filename}** — ${doc.document_type || 'Unknown type'} (${Math.round(doc.extraction_confidence * 100)}% confidence)\n`
        })

        if (result.consolidated_extraction) {
          const ext = result.consolidated_extraction
          summary += `\n**Consolidated extraction:**\n`
          if (ext.title) summary += `- Title: ${ext.title}\n`
          if (ext.amount) summary += `- Amount: $${Number(ext.amount).toLocaleString()}\n`
          if (ext.vendor_name) summary += `- Vendor: ${ext.vendor_name}\n`
        }

        summary += `\nWould you like me to create a Purchase Request from these documents?`
        addMessage({ role: 'assistant', content: summary })
      }
    } catch (err) {
      addMessage({
        role: 'assistant',
        content: `I had trouble processing the upload: ${err instanceof Error ? err.message : 'Unknown error'}. Please try again or use the **Create New PR** button on the Purchase Explorer page.`,
      })
    } finally {
      setIsTyping(false)
    }
  }, [addMessage, chatHistory, setIsTyping])

  // Handle quick action click
  const handleQuickAction = (action: string) => {
    handleSendMessage(action)
  }

  // Get page name from context
  const getPageName = () => {
    if (currentPageContext === '/') return 'Dashboard'
    const segments = currentPageContext.split('/').filter(Boolean)
    return segments.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' > ')
  }

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeManager()
    }
  }

  // Only render in browser (for portal)
  if (typeof window === 'undefined') return null

  return createPortal(
    <AnimatePresence mode="wait">
      {isManagerOpen && (
        <>
          {/* Backdrop with blur - covers entire viewport including sidebar */}
          <motion.div
            key="ai-manager-backdrop"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2 }}
            onClick={handleBackdropClick}
            className="fixed inset-0 z-modal-backdrop flex items-center justify-center p-4 sm:p-8 md:p-12"
            style={{
              backgroundColor: 'rgba(26, 35, 64, 0.5)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
            aria-hidden="true"
          >
            {/* Modal Container - centered and properly sized */}
            <motion.div
              ref={modalRef}
              key="ai-manager-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ai-manager-title"
              aria-describedby="ai-manager-description"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                // Z-index for proper stacking
                'z-modal',
                // Size and positioning
                'relative w-full max-w-2xl',
                'h-[80vh] max-h-[700px] min-h-[450px]',
                // Solid white background for better readability
                'bg-white',
                // Border and shadow
                'border border-gray-200',
                'rounded-2xl',
                'shadow-2xl',
                // Layout
                'flex flex-col',
                'overflow-hidden'
              )}
            >
              {/* Decorative gradient border */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-brand-cornflower/20 via-transparent to-brand-purple/20 pointer-events-none" />
              
              {/* Header */}
              <div className="relative border-b border-border/30 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* AI Icon */}
                    <div className={cn(
                      'flex h-12 w-12 items-center justify-center rounded-xl',
                      'bg-gradient-to-br from-brand-navy via-brand-purple to-brand-cornflower',
                      'shadow-lg shadow-brand-purple/25',
                      'ring-2 ring-white/50'
                    )}>
                      <Icons.sparkles className="h-6 w-6 text-white" strokeWidth={1.5} />
                    </div>
                    
                    <div>
                      <h2 
                        id="ai-manager-title"
                        className="text-xl font-semibold text-brand-navy"
                      >
                        Supervity AI
                      </h2>
                      <p 
                        id="ai-manager-description"
                        className="text-sm text-muted-foreground"
                      >
                        Your intelligent command center assistant
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {chatHistory.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearHistory}
                        className="text-muted-foreground hover:text-foreground gap-1.5"
                        title="Clear conversation"
                      >
                        <Icons.trash className="h-4 w-4" strokeWidth={1.5} />
                        <span className="hidden sm:inline">Clear</span>
                      </Button>
                    )}
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={closeManager}
                      className="text-muted-foreground hover:text-foreground hover:bg-red-50 hover:text-red-600"
                      title="Close (Esc)"
                    >
                      <Icons.close className="h-5 w-5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>

                {/* Page Context Badge */}
                <div className="mt-4 flex items-center gap-3">
                  <div className={cn(
                    'inline-flex items-center gap-2 rounded-full px-3 py-1.5',
                    'bg-gradient-to-r from-brand-cornflower/10 to-brand-purple/10',
                    'text-brand-navy border border-brand-cornflower/20',
                    'text-sm font-medium'
                  )}>
                    <Icons.globe className="h-4 w-4 text-brand-cornflower" strokeWidth={1.5} />
                    <span>Viewing: {getPageName()}</span>
                  </div>
                  
                  <kbd className={cn(
                    'hidden items-center rounded-md px-2 py-1 sm:inline-flex',
                    'text-xs font-medium',
                    'border border-border/50 bg-muted/50 text-muted-foreground'
                  )}>
                    <Icons.command className="h-3 w-3 mr-1" />
                    J to toggle
                  </kbd>
                </div>
              </div>

              {/* Messages Area */}
              <div className="relative flex-1 overflow-y-auto px-6 py-5">
                {chatHistory.length === 0 ? (
                  /* Empty State */
                  <div className="flex h-full flex-col items-center justify-center text-center py-8">
                    <div className={cn(
                      'mb-6 flex h-20 w-20 items-center justify-center rounded-2xl',
                      'bg-gradient-to-br from-brand-cornflower/20 to-brand-purple/20',
                      'ring-1 ring-brand-cornflower/30'
                    )}>
                      <Icons.messageSquare className="h-10 w-10 text-brand-cornflower" strokeWidth={1.5} />
                    </div>
                    
                    <h3 className="font-display text-xl font-semibold text-brand-navy">
                      How can I help you?
                    </h3>
                    <p className="mt-2 max-w-sm text-muted-foreground">
                      Ask me anything about your data, or use a quick action below.
                    </p>

                    {/* Quick Actions - Capability Bubbles */}
                    <div className="mt-8 w-full max-w-lg">
                      <CapabilityBubbles onSelect={handleQuickAction} currentPage={currentPageContext} />
                    </div>
                  </div>
                ) : (
                  /* Chat Messages */
                  <div className="space-y-4">
                    {chatHistory.map((message) => (
                      <ChatMessage
                        key={message.id}
                        message={message}
                        userName={session?.user?.name || undefined}
                        userImage={session?.user?.image}
                      />
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Input Area */}
              <div className="relative border-t border-border/30">
                <ChatInput
                  onSend={handleSendMessage}
                  onFileUpload={handleFileUpload}
                  disabled={isTyping}
                  placeholder={isTyping ? 'AI is thinking...' : 'Ask anything or upload a document...'}
                />
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  )
}
